import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactApexChart from 'react-apexcharts';

import moment from 'moment-timezone';
import { Refresh } from '@saleshandy/icons';
import { SkeletonLoading } from '@saleshandy/design-system';
import { Spinner } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { getPercentageValue } from '../../../home/components/header/utils/helper';
import {
  aggregateData,
  getGraphSeries,
  graphColorsArray,
  graphLegends,
  timeFrameOptions,
} from './utils/helper';
import { Granularity } from '../../../reports/enums/reports';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import { Images } from '../../../../shared/app-constants';
import {
  GraphLegendHorizontalAlignment,
  GraphLegendPosition,
} from './utils/enum';
import DateFilter from '../../../../shared/components/date-filter/date-filter';

const VerticalTimeline = ({ text }) => (
  <div className="vertical-line">
    <div className="timeline-line" />
    <div className="timeline-label">{text}</div>
    <div className="timeline-line" />
  </div>
);

const IRChart = React.memo(
  ({
    gData,
    graphHeight,
    selectedLegends,
    setSelectedLegends,
    mapSeriesData,
    legendPosition,
    legendHorizontalAlignment,
    setMaxYAxis,
    maxYAxis,
    calculateMaxYAxis,
  }: {
    gData: any;
    graphHeight: number;
    selectedLegends: any;
    setSelectedLegends: any;
    mapSeriesData: any;
    legendPosition: GraphLegendPosition;
    legendHorizontalAlignment: GraphLegendHorizontalAlignment;
    setMaxYAxis: React.Dispatch<React.SetStateAction<number>>;
    maxYAxis: number;
    calculateMaxYAxis: (data: any, additionalPercent?: number) => number;
  }) => {
    const chartRef = useRef(null);
    const totalXLabels = gData?.dates?.length - 2;
    const maxXLabels = 6;

    const formatDateToUtc = (value) => {
      const dateTime = moment(new Date(value).toISOString());
      return dateTime.utcOffset(0).format('DD MMM, YY');
    };

    const calculateGraphToolTipData = (i: number, dataPointIndex: number) => {
      if (!mapSeriesData[i]) {
        return 0;
      }
      const data = mapSeriesData[i][dataPointIndex] || 0;
      return data?.toLocaleString();
    };

    const calculateGraphToolTipPercentageValue = (
      i: number,
      dataPointIndex: number,
    ) => {
      const baseValue = mapSeriesData[4][dataPointIndex];
      const data = Number(calculateGraphToolTipData(i, dataPointIndex));
      const percentageValue =
        getPercentageValue(data, baseValue)?.toFixed(2) || 0;

      return `${percentageValue}%`;
    };

    const options: any = {
      colors: graphColorsArray,
      chart: {
        id: 'graph-report',
        type: 'area',
        toolbar: {
          show: false, // This hides the top-right icons
        },
        zoom: {
          type: 'x',
          enabled: true,
          autoScaleYaxis: true,
        },
        events: {
          legendClick(_chartContext, seriesIndex) {
            const names = graphLegends;
            const isLegendPresent = selectedLegends[names[seriesIndex]];
            const updatedLegends = { ...selectedLegends };
            if (isLegendPresent) {
              updatedLegends[names[seriesIndex]] = false;
            } else {
              updatedLegends[names[seriesIndex]] = true;
            }
            // Only include series data for selected legends
            const selectedSeriesData = [];
            if (updatedLegends.Inbox) {
              selectedSeriesData.push(...Array.from(mapSeriesData[0]));
            }
            if (updatedLegends.Spam) {
              selectedSeriesData.push(...Array.from(mapSeriesData[1]));
            }
            if (updatedLegends.Other) {
              selectedSeriesData.push(...Array.from(mapSeriesData[2]));
            }
            if (updatedLegends.Undelivered) {
              selectedSeriesData.push(...Array.from(mapSeriesData[3]));
            }
            setMaxYAxis(calculateMaxYAxis(selectedSeriesData));
            setSelectedLegends(updatedLegends);
          },
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        curve: 'straight',
        width: [2, 0, 0, 2],
        lineCap: 'round',
      },
      grid: {
        show: true,
        borderColor: '#E5E7EB',
        strokeDashArray: 2,
        position: 'back',
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      markers: {
        colors: graphColorsArray,
        size: [2, 0, 0, 2],
        shape: 'circle',
        hover: {
          size: 4,
        },
        strokeWidth: 0,
      },
      xaxis: {
        type: 'datetime',
        categories: gData?.dates,
        tickAmount: totalXLabels > maxXLabels ? maxXLabels : totalXLabels,
        tickPlacement: 'on',
        labels: {
          formatter(value) {
            return value ? formatDateToUtc(value) : '';
          },
          style: {
            color: '#4B5563',
            fontSize: '10px',
            fontFamily: 'Inter',
            fontWeight: 400,
          },
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: [
        {
          min: 0,
          max: maxYAxis,
          tickPlacement: 'on',
          tickAmount: 5,
          labels: {
            offsetX: -10,
            style: {
              colors: ['#4B5563'],
              fontFamily: 'Inter',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '1rem',
            },
            formatter: (val) => {
              if (!val) {
                return '0';
              }

              return val.toFixed(0);
            },
          },
        },
      ],
      tooltip: {
        x: {
          show: false,
          formatter: (value) => new Date(value).toLocaleDateString(),
        },
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const names = graphLegends;
          // const percentages = [40, 1.2, 0.8, 0.7, 0.5, 0.5]; // Replace these with your dynamic percentages
          const colors = graphColorsArray;

          const timestamp = w.globals?.seriesX[seriesIndex][dataPointIndex];

          let tooltipContent = `<div style="width: 310px; padding-bottom: 4px; border-radius: 8px; box-shadow: 3px 8px 24px -6px rgba(15, 34, 67, 0.16);">
          <div style="padding: 12px 16px; color: #6B7280; font-size: 14px; font-weight: 400; line-height: 16px; margin-bottom: 16px; border-bottom: 1px solid #F3F4F6; background: #F9FAFB;">${formatDateToUtc(
            timestamp,
          )}</div>
          <div style="display: flex; justify-content: space-between; align-items: center; padding-left: 16px; padding-right: 16px; margin-bottom: 12px">
                <div style="display: flex; align-items: center;">
                  <span style="display: inline-block; width: 3px; height: 24px; background-color:#60A5FA; border-radius: 4px; margin-right: 8px;"></span>
                  <span style="font-size: 14px; color: #666;">Sent</span>
                </div>
                <div style="display: flex; align-items: center;">
                  <span style="font-size: 14px; font-weight: bold; color: #333;">${
                    mapSeriesData[4][dataPointIndex]
                  }</span>
                </div>
              </div>
          `;

          series.forEach((s, i) => {
            if (series[i][dataPointIndex] !== undefined) {
              tooltipContent += `
              <div style="display: flex; justify-content: space-between; align-items: center; padding-left: 16px; padding-right: 16px; margin-bottom: 12px">
                <div style="display: flex; align-items: center;">
                  <span style="display: inline-block; width: 3px; height: 24px; background-color: ${
                    colors[i]
                  }; border-radius: 4px; margin-right: 8px;"></span>
                  <span style="font-size: 14px; color: #666;">${names[i]}</span>
                </div>
                <div style="display: flex; align-items: center;">
                  <span style="font-size: 14px; font-weight: bold; color: #333;">${calculateGraphToolTipData(
                    i,
                    dataPointIndex,
                  )}</span>
                  <span style="font-size: 12px; color: #999; margin-left: 5px;"> ${calculateGraphToolTipPercentageValue(
                    i,
                    dataPointIndex,
                  )}</span>
                </div>
              </div>`;
            }
          });

          tooltipContent += `</div>`;
          return tooltipContent;
        },
        legend: {
          position: 'bottom',
          color: '#4B5563',
          fontFamily: 'Inter, sans-serif',
          fontSize: '12px',
          fontweight: 500,
          markers: {
            size: 5,
            offsetX: -5,
          },
          itemMargin: {
            horizontal: 16,
            vertical: 10,
          },
        },
      },
      legend: {
        labels: {
          style: {
            colors: ['#4B5563'],
            fontFamily: 'Inter',
            fontSize: '12px',
            fontWeight: 500,
          },
        },
        markers: {
          size: 5.5,
          offsetX: -4,
        },
        position: legendPosition,
        horizontalAlign: legendHorizontalAlignment,
      },
      fill: {
        type: ['gradient', 'solid', 'solid', 'gradient'],
        opacity: 0.5,
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0,
          stops: [0, 100],
        },
      },
    };
    const series = getGraphSeries(gData);
    const seriesWithVisibility = series.map((s) => ({
      ...s,
      hidden: !selectedLegends[s.name],
    }));

    return (
      <div className="inbox-radar-chart-container">
        <div className="inbox-radar-chart">
          <VerticalTimeline text="Placement Score" />
          <ReactApexChart
            ref={chartRef}
            options={options}
            series={seriesWithVisibility}
            type="area"
            height={graphHeight}
            className="inbox-radar-graph"
          />
        </div>
      </div>
    );
  },
);

const IRGraph: React.FC<{
  testGraphData: any;
  filters: any;
  isLoading: boolean;
  showError: boolean;
  graphHeight?: number;
  legendPosition?: GraphLegendPosition;
  legendHorizontalAlignment?: GraphLegendHorizontalAlignment;
  showDateFilter?: boolean;
  onDateTimeSelectionChange?: (
    label: string,
    startDate: DateTime,
    endDate: DateTime,
    displayText: string,
  ) => void;
  handleRefresh?: () => void;
  disableAnimation?: boolean;
  dashboardGraph?: boolean;
}> = ({
  testGraphData,
  filters,
  isLoading,
  showError,
  graphHeight = 342,
  legendPosition = GraphLegendPosition.bottom,
  legendHorizontalAlignment = GraphLegendHorizontalAlignment.center,
  showDateFilter = false,
  onDateTimeSelectionChange,
  handleRefresh,
  disableAnimation,
  dashboardGraph = false,
}) => {
  const [selectedLegends, setSelectedLegends] = useState<{
    Inbox: boolean;
    Spam: boolean;
    Other: boolean;
    Undelivered: boolean;
  }>({
    Inbox: true,
    Spam: true,
    Other: true,
    Undelivered: false,
  });
  const daysDifference = useMemo(
    () => filters?.date?.endDate?.diff(filters?.date?.startDate, ['days']),
    [filters],
  );

  const calculateMaxYAxis = (data, additionalPercent = 10) => {
    const max = Math.max(...data);
    return Math.ceil(max + (max / 100) * additionalPercent);
  };

  const [chartConfig, setChartConfig] = useState({
    interval: timeFrameOptions.find((opt) => opt.applicable(daysDifference))
      ?.value,
    isFirstTimeEvent: true,
    isRelative: true,
  });

  const [maxYAxis, setMaxYAxis] = useState(0);

  const gData = useMemo(
    () => aggregateData(testGraphData, chartConfig.interval),
    [testGraphData, chartConfig.interval],
  );

  const mapSeriesData = useMemo(() => {
    if (!gData) return [];
    // Only include series data for selected legends
    const selectedSeriesData = [];
    if (selectedLegends.Inbox) {
      selectedSeriesData.push(...Array.from(gData?.inbox));
    }
    if (selectedLegends.Spam) {
      selectedSeriesData.push(...Array.from(gData?.spam));
    }
    if (selectedLegends.Other) {
      selectedSeriesData.push(...Array.from(gData?.others));
    }
    if (selectedLegends.Undelivered) {
      selectedSeriesData.push(...Array.from(gData?.notDelivered));
    }
    setMaxYAxis(calculateMaxYAxis(selectedSeriesData));

    return [
      gData?.inbox || [],
      gData?.spam || [],
      gData?.others || [],
      gData?.notDelivered || [],
      gData?.sentEmails || [],
    ];
  }, [gData]);

  const handleConfigChange = useCallback(
    (key, value) => {
      const selectedSeriesData = [];
      if (selectedLegends?.Inbox) {
        selectedSeriesData.push(...Array.from(mapSeriesData[0]));
      }
      if (selectedLegends?.Spam) {
        selectedSeriesData.push(...Array.from(mapSeriesData[1]));
      }
      if (selectedLegends?.Other) {
        selectedSeriesData.push(...Array.from(mapSeriesData[2]));
      }
      if (selectedLegends?.Undelivered) {
        selectedSeriesData.push(...Array.from(mapSeriesData[3]));
      }
      setMaxYAxis(calculateMaxYAxis(selectedSeriesData));
      setChartConfig((prevConfig) => ({
        ...prevConfig,
        [key]: value,
      }));
    },
    [mapSeriesData],
  );

  useEffect(() => {
    const initialSelectedLegends: {
      Inbox: boolean;
      Spam: boolean;
      Other: boolean;
      Undelivered: boolean;
    } = graphLegends.reduce(
      (acc, legend) => {
        if (legend === 'Undelivered') {
          acc[legend] = false;
        } else {
          acc[legend] = true;
        }
        return acc;
      },
      {
        Inbox: false,
        Spam: false,
        Other: false,
        Undelivered: false,
      },
    );
    setSelectedLegends(initialSelectedLegends);
  }, []);

  const getTimeFrameClass = (
    isApplicable: boolean,
    interval: Granularity,
    value: Granularity,
  ) => {
    if (!isApplicable) {
      return 'time-frame__disabled';
    }

    if (interval === value) {
      return 'time-frame__active';
    }

    return '';
  };

  const getTimeFrameTextClass = (
    isApplicable: boolean,
    interval: Granularity,
    value: Granularity,
  ) => {
    if (!isApplicable) {
      return 'time-frame__item__text';
    }
    if (interval === value) {
      return 'blue-txt-11';
    }

    return 'gray-txt-15';
  };

  if (isLoading) {
    return (
      <div className="inbox-radar-graph-container">
        <div className="inbox-radar-graph-header">
          <div className="graph-filters__middle">
            <div className="d-flex time-frame">
              <SkeletonLoading
                height={24}
                width={71}
                enableAnimation={!disableAnimation}
              />
              <SkeletonLoading
                height={24}
                width={71}
                enableAnimation={!disableAnimation}
              />
              <SkeletonLoading
                height={24}
                width={71}
                enableAnimation={!disableAnimation}
              />
              <SkeletonLoading
                height={24}
                width={71}
                enableAnimation={!disableAnimation}
              />
            </div>
          </div>
          <div className="graph-filters__right">
            <SkeletonLoading
              height={32}
              width={202}
              enableAnimation={!disableAnimation}
            />
          </div>
        </div>
        {!disableAnimation && (
          <div className="graph-loading-container">
            <div className="graph-loading-inner">
              <Spinner animation="border" />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="inbox-radar-graph-container">
      <div className="inbox-radar-graph-header">
        <div className="graph-filters__middle">
          <div className="d-flex time-frame">
            {timeFrameOptions.map(({ label, value, applicable }) => (
              <div
                key={label}
                className={`time-frame__item ${getTimeFrameClass(
                  applicable(daysDifference),
                  chartConfig.interval,
                  value,
                )}`}
                {...accessibleOnClick(() => {
                  handleConfigChange('interval', value);
                })}
              >
                <span
                  className={`regular-1 font-medium line-height-18 ${getTimeFrameTextClass(
                    applicable(daysDifference),
                    chartConfig.interval,
                    value,
                  )}`}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
        {showDateFilter && (
          <div
            className={`graph-filters__right ${
              dashboardGraph ? 'graph-filters__dashboard' : ''
            }`}
          >
            <DateFilter
              selectedDateRangeOption={filters.date?.label}
              startDate={filters.date?.startDate}
              endDate={filters.date?.endDate}
              displayText={filters.date?.displayText}
              onDateTimeSelectionChange={onDateTimeSelectionChange}
              shouldHaveMinMaxValueLimit={false}
              shouldShowDateTooltip={true}
              shouldHaveMaxValueLimit={true}
            />
          </div>
        )}
      </div>
      {!showError && !isLoading && gData && (
        <IRChart
          gData={gData}
          graphHeight={graphHeight}
          selectedLegends={selectedLegends}
          setSelectedLegends={setSelectedLegends}
          mapSeriesData={mapSeriesData}
          legendPosition={legendPosition}
          legendHorizontalAlignment={legendHorizontalAlignment}
          setMaxYAxis={setMaxYAxis}
          maxYAxis={maxYAxis}
          calculateMaxYAxis={calculateMaxYAxis}
        />
      )}
      {showError && (
        <div className="no-data-container">
          <img src={Images.NoResultInboxRadar} alt="No data" />
          <div className="no-data-text">
            <span>Something went wrong, please try again.</span>
          </div>
          <div className="refresh-button" {...accessibleOnClick(handleRefresh)}>
            <Refresh width={20} height={20} color="#1D4ED8" />
            <span>Refresh</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(IRGraph);
